<template>
  <div>
    <div v-if="isAtInitialization()">
      <PasswordResetInitialization
        :on-email-sent="onEmailSent"
      ></PasswordResetInitialization>
    </div>
    <div v-if="isAtCodeEntry()">
      <PasswordResetCodeInput
        :on-reset-code-received="onResetCodeReceived"
        :username="username"
      ></PasswordResetCodeInput>
    </div>
    <div v-if="isAtPasswordEntry()">
      <PasswordResetNewPassword
        :account-id="accountId"
        :reset-code="resetCode"
        @onPasswordReset="onPasswordReset"
      ></PasswordResetNewPassword>
    </div>
    <div v-if="isAfterPasswordReset()">
      <Card :footerDivider="false" :title="$t('passwordChanged.title')">
        <template v-slot:content>
          {{ $t('passwordChanged.redirecting', { remainingSecondsBeforeRedirect }) }}
        </template>
        <template v-slot:footer>
          <div class="footer-cta--left">
            <CustomButton
              @click="navigateToLoginPage"
              :buttonColor="ButtonColor.LINK"
              buttonId="password-reset.forgot-username-link"
            >
              {{ $t('passwordChanged.cta') }}
            </CustomButton>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script lang="ts" setup>
import PasswordResetCodeInput from '@/components/account-management/publicpasswordreset/PublicPasswordResetCodeInput.vue'
import PasswordResetInitialization from '@/components/account-management/publicpasswordreset/PublicPasswordResetInitialization.vue'
import PasswordResetNewPassword from '@/components/account-management/publicpasswordreset/PublicPasswordResetNewPassword.vue'
import { ButtonColor, Card, CustomButton } from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { ref } from 'vue'
import { keycloakLogout } from '@/utils/KeycloakUtils'
import { useI18n } from 'vue-i18n'

const { t: $t } = useI18n()

enum ResetState {
  INITIALIZATION,
  RESET_CODE_ENTRY,
  PASSWORD_ENTRY,
  PASSWORD_CHANGED
}

const username = ref('')
const resetStatus = ref(ResetState.INITIALIZATION)
const accountId = ref('')
const resetCode = ref('')
const remainingSecondsBeforeRedirect = ref(5)

const isAtInitialization = (): boolean => {
  return resetStatus.value === ResetState.INITIALIZATION
}

const isAtCodeEntry = (): boolean => {
  return resetStatus.value === ResetState.RESET_CODE_ENTRY
}

const isAtPasswordEntry = (): boolean => {
  return resetStatus.value === ResetState.PASSWORD_ENTRY
}

const isAfterPasswordReset = (): boolean => {
  return resetStatus.value === ResetState.PASSWORD_CHANGED
}

const onEmailSent = (
  _username: string,
  _responseHeaders: { [header: string]: string } = {}
): void => {
  username.value = _username
  resetStatus.value = ResetState.RESET_CODE_ENTRY
}

const onResetCodeReceived = (_accountId: string, _resetCode: string): void => {
  if (!_accountId || !_resetCode) {
    console.error('Invalid response')
  }
  accountId.value = _accountId
  resetCode.value = _resetCode
  resetStatus.value = ResetState.PASSWORD_ENTRY
}

const defaultSecondsBeforeRedirect = (): number => {
  const envDefinedSecondsBeforeRedirect: string | null | undefined =
    process.env.VUE_APP_REDIRECT_AFTER_PASSWORD_CHANGE_WAITTIME_IN_SECONDS
  if (
    envDefinedSecondsBeforeRedirect === undefined ||
    envDefinedSecondsBeforeRedirect === null ||
    !/^\d+$/.test(envDefinedSecondsBeforeRedirect)
  ) {
    return 5
  }
  return Number.parseInt(envDefinedSecondsBeforeRedirect, 10)
}

const onPasswordReset = (): void => {
  resetStatus.value = ResetState.PASSWORD_CHANGED

  remainingSecondsBeforeRedirect.value = defaultSecondsBeforeRedirect()
  setInterval(() => {
    remainingSecondsBeforeRedirect.value--
  }, 1000)

  setTimeout(() => {
    navigateToLoginPage()
  }, defaultSecondsBeforeRedirect() * 1000)
}

const navigateToLoginPage = () => {
  if (globalThis.keycloak && globalThis.keycloak.authenticated) {
    keycloakLogout(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_REDIRECT)
  } else {
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE, true)
  }
}
</script>
